/**
 * Generato automaticamente.
 * framework-cmp-procedimenti
 */
import { useMutation, useQuery } from 'react-query';
import type {
  MutationFunction,
  QueryFunction,
  QueryKey,
  UseMutationOptions,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';
import { apiClientRequest } from '../../../client/apiClient';
import type { ErrorType } from '../../../client/apiClient';
import type {
  GetBackScadenzeParams,
  ModificaScadenzaRequest,
  ScadenzaDTO,
  WsRispostaPaginataListScadenzaDTO,
} from '.././schemas';

type AwaitedInput<T> = PromiseLike<T> | T;

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;

// eslint-disable-next-line
type SecondParameter<T extends (...args: any) => any> = T extends (
  config: any,
  args: infer P,
) => any
  ? P
  : never;

export const putBackScadenzeId = (
  idScadenza: string,
  modificaScadenzaRequest: ModificaScadenzaRequest,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<ScadenzaDTO>(
    {
      url: `/framework-bo-procedimenti/scadenze/${idScadenza}`,
      method: 'put',
      headers: { 'Content-Type': 'application/json' },
      data: modificaScadenzaRequest,
    },
    options,
  );
};

export const getPutBackScadenzeIdMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putBackScadenzeId>>,
    TError,
    { idScadenza: string; data: ModificaScadenzaRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof putBackScadenzeId>>,
  TError,
  { idScadenza: string; data: ModificaScadenzaRequest },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof putBackScadenzeId>>,
    { idScadenza: string; data: ModificaScadenzaRequest }
  > = props => {
    const { idScadenza, data } = props ?? {};

    return putBackScadenzeId(idScadenza, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PutBackScadenzeIdMutationResult = NonNullable<
  Awaited<ReturnType<typeof putBackScadenzeId>>
>;
export type PutBackScadenzeIdMutationBody = ModificaScadenzaRequest;
export type PutBackScadenzeIdMutationError = ErrorType<unknown>;

export const usePutBackScadenzeId = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putBackScadenzeId>>,
    TError,
    { idScadenza: string; data: ModificaScadenzaRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getPutBackScadenzeIdMutationOptions(options);

  return useMutation(mutationOptions);
};
export const getBackScadenze = (
  params?: GetBackScadenzeParams,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<WsRispostaPaginataListScadenzaDTO>(
    {
      url: `/framework-bo-procedimenti/scadenze`,
      method: 'get',
      params,
      signal,
    },
    options,
  );
};

export const getGetBackScadenzeQueryKey = (params?: GetBackScadenzeParams) =>
  [`/framework-bo-procedimenti/scadenze`, ...(params ? [params] : [])] as const;

export const getGetBackScadenzeQueryOptions = <
  TData = Awaited<ReturnType<typeof getBackScadenze>>,
  TError = ErrorType<unknown>,
>(
  params?: GetBackScadenzeParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getBackScadenze>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getBackScadenze>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetBackScadenzeQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getBackScadenze>>> = ({
    signal,
  }) => getBackScadenze(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions };
};

export type GetBackScadenzeQueryResult = NonNullable<
  Awaited<ReturnType<typeof getBackScadenze>>
>;
export type GetBackScadenzeQueryError = ErrorType<unknown>;

export const useGetBackScadenze = <
  TData = Awaited<ReturnType<typeof getBackScadenze>>,
  TError = ErrorType<unknown>,
>(
  params?: GetBackScadenzeParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getBackScadenze>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetBackScadenzeQueryOptions(params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};
